import { Converter, StringUtil } from '@/util'
import { IEquipmentMasterDataDTO } from './IEquipmentMasterDataDTO'
import { IPartnerAccountDTO } from '../partnerAccount'
import { IEquipmentSoftwareDTO } from './IEquipmentSoftwareDTO'

export class EquipmentMasterData implements IEquipmentMasterDataDTO {
    private localize?: (id: string) => string
    constructor(source: IEquipmentMasterDataDTO, localizer?: ((id: string) => string)) {
        this.equipmentNo = source.equipmentNo
        this.description = source.description
        this.materialNo = source.materialNo
        this.serialNo = source.serialNo
        this.productModel = source.productModel
        this.productionOrderNo = source.productionOrderNo
        this.salesOrderNo = source.salesOrderNo
        this.salesOrderRef = source.salesOrderRef
        this.contactPersonFullName = source.contactPersonFullName
        this.contactPersonEmail = source.contactPersonEmail
        this.deliveryDateIsoString = source.deliveryDateIsoString
        this.soldTo = source.soldTo
        this.internalNote = source.internalNote
        this.software = source.software
        this.adminOnly = source.adminOnly
        this.noBomSync = source.noBomSync

        this.localize = localizer
    }

    readonly equipmentNo: number
    readonly description: string
    readonly materialNo: number
    readonly serialNo: string
    readonly productModel: string
    readonly productionOrderNo?: number
    readonly salesOrderNo?: number
    readonly salesOrderRef?: string
    readonly contactPersonFullName?: string
    readonly contactPersonEmail?: string
    readonly deliveryDateIsoString?: string
    readonly soldTo?: IPartnerAccountDTO
    readonly internalNote?: string
    readonly software?: IEquipmentSoftwareDTO
    readonly adminOnly: boolean
    readonly noBomSync: boolean
    get soldToInfo(): string {
        return `${this.soldTo?.accountNo} ${this.soldTo?.companyName}`
    }

    get contactPersonInfo(): string {
        if (StringUtil.hasContent(this.contactPersonFullName) && StringUtil.hasContent(this.contactPersonEmail)) {
            return `${this.contactPersonFullName} (${this.contactPersonEmail})`
        }

        if (StringUtil.hasContent(this.contactPersonFullName)) {
            return this.contactPersonFullName ?? ''
        }

        if (StringUtil.hasContent(this.contactPersonEmail)) {
            return this.contactPersonEmail ?? ''
        }

        return '-'
    }

    get deliveryDate(): Date | undefined {
        const d = Converter.toLocalDate(this.deliveryDateIsoString ?? null)
        d?.setHours(0, 0, 0, 0)
        return d
    }

    get installedVersion(): string {
        return this.software?.versionInstalled || '-'
    }

    get softwareStatus(): string {
        let result: string

        if (this.noSoftware) return '-'
        else if (this.isUpToDate) result = 'NoUpgradeAvailable'
        else if (this.isMinorUpgrade) result = 'MinorUpgradeAvailable'
        else if (this.isMajorUpgrade) result = 'ImportantUpgradeAvailable'
        else return '-'

        return this.localize
            ? this.localize(result)
            : result
    }

    get availableVersion(): string {
        return this.software?.versionAvailable || ''
    }

    private buildOf(version: string): number | null {
        if (!version) return null
        const parts = version.split('.')
        const build = parts[parts.length - 1]
        const n = parseInt(build)
        return isNaN(n) ? null : n
    }

    get installedBuild(): number | null {
        return this.buildOf(this.installedVersion)
    }

    get availableBuild(): number | null {
        return this.buildOf(this.availableVersion)
    }

    get noSoftware(): boolean {
        return !this.installedVersion
    }

    get isUpToDate(): boolean {
        if (this.noSoftware) return false // no software installed
        if (!this.availableVersion) return true // nothing available
        if (this.installedBuild == null || this.availableBuild == null) return true
        return this.installedBuild >= this.availableBuild
    }

    get isUpgradeAvailable(): boolean {
        return !this.isUpToDate && !this.noSoftware && !!this.availableVersion
    }

    get isMinorUpgrade(): boolean {
        if (!this.isUpgradeAvailable) return false
        // Minor upgrade: only build differs
        const baseInstalled = this.installedVersion.substring(0, this.installedVersion.lastIndexOf('.'))
        const baseAvailable = this.availableVersion.substring(0, this.availableVersion.lastIndexOf('.'))
        return baseInstalled === baseAvailable
    }

    get isMajorUpgrade(): boolean {
        return this.isUpgradeAvailable && !this.isMinorUpgrade
    }
}
